import { cn } from "@/shared/lib/utils";
import { LucideIcon, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

interface ModuleCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  href: string;
  stats?: {
    label: string;
    value: string;
  };
  status?: "active" | "pending" | "idle";
  className?: string;
}

export function ModuleCard({
  title,
  description,
  icon: Icon,
  href,
  stats,
  status = "idle",
  className,
}: ModuleCardProps) {
  const statusBadge = {
    active: { text: "Active", className: "bg-success/10 text-success" },
    pending: { text: "Pending", className: "bg-warning/10 text-warning" },
    idle: { text: "Idle", className: "bg-muted text-muted-foreground" },
  };

  return (
    <Link
      to={href}
      className={cn(
        "group block bg-card rounded-xl p-6 shadow-card border border-border transition-all duration-300",
        "hover:shadow-elevated hover:border-primary/30 hover:-translate-y-1",
        className
      )}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 rounded-xl gradient-industrial flex items-center justify-center shadow-industrial">
          <Icon className="w-6 h-6 text-primary-foreground" />
        </div>
        <span
          className={cn(
            "px-2.5 py-1 rounded-full text-xs font-medium",
            statusBadge[status].className
          )}
        >
          {statusBadge[status].text}
        </span>
      </div>

      <h3 className="text-lg font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
        {title}
      </h3>
      <p className="text-sm text-muted-foreground mb-4">{description}</p>

      {stats && (
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{stats.label}</span>
            <span className="text-sm font-semibold text-foreground">{stats.value}</span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        <span>Open Module</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </div>
    </Link>
  );
}
